import React from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import YouTube from 'react-youtube'
import { ArrowUpRight, Check, Film, ListMusic, PictureInPicture2, Play } from 'lucide-react'
import { mediaBucketsEnabled, RESERVED_YOUTUBE_EMBED_HEIGHT } from 'lib/mediaBuckets'
import { hostedYouTubePlaybackEnabled } from 'lib/hostedMediaPlayback'
import { mediaDockCinemaEnabled } from 'lib/mediaDockCinema'
import LazyYoutube from 'components/shared/LazyYoutube'
import TvMinimalPlayIcon from 'components/shared/icons/TvMinimalPlayIcon'
import VideoChrome from 'components/shared/VideoChrome'
import BsvTransactionStatusIcon from 'components/shared/BsvTransactionStatusIcon'
import {
    openTrackInCinema,
    playTrackInDock,
    toggleTrackInPlaylist,
} from 'components/shared/MediaDock/mediaDockEvents'
import { PLAY_IN_DOCK_LABEL } from 'components/shared/MediaDock/mediaDockPlayInDock'
import { playlistMenuLabel } from 'components/shared/MediaDock/useMediaKeyInPlaylist'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'
import { safeHttpHref } from 'lib/safeHttpHref'
import { suppressMediaPayloadContentClick } from './mediaPayloadClickPropagation'
import {
    buildInlineYouTubePlayerVars,
    disableYouTubeCaptions,
    readYouTubeCaptionsState,
    restoreYouTubeCaptions,
    setYouTubeCaptionsEnabled,
    shouldClaimInlineYouTubePlayback,
    subscribeToYouTubeCaptionsState,
} from 'components/shared/MediaDock/dockYouTubePlayer'
import {
    claimMediaPlayback,
    clearMediaTrackOrigin,
    createMediaPlaybackOwnerId,
    findMountedOwnerForMediaKey,
    getMediaDockHostedChromeLayer,
    getMediaDockHostedSurfaceElement,
    getMediaOwnerControlSnapshot,
    getMediaOwnerPlaybackSnapshot,
    getMediaPlaybackState,
    isElementUserVisible,
    isInlineMediaPlaybackHandoffPendingForAnswer,
    isMediaKeyPlayingInDock,
    isMediaKeyPresentedInDock,
    isMediaOriginHostedInline,
    isMediaPlaybackHandoffSourceOwner,
    isMediaTrackInPlaylist,
    pauseMediaKey,
    playMediaKey,
    playMediaPlaybackOwner,
    prepareActiveMediaPlaybackForInlineHandoff,
    releaseMediaPlayback,
    registerMediaTrack,
    requestHostedMediaPlayback,
    resumePendingInlineMediaPlayback,
    seekMediaKey,
    setMediaKeyCaptionsEnabled,
    setMediaKeyPlaybackRate,
    setMediaKeyVolume,
    setMediaPlaybackState,
    setMediaTrackOrigin,
    subscribeToMediaDock,
    syncMediaPlaybackOwner,
    unregisterMediaPlaybackOwner,
} from 'state/mediaPlaybackCoordinator'
import dockStyles from 'components/shared/MediaDock/media-dock.module.scss'
import styles from './answer.module.scss'

const clampYouTubeVolume = value => {
    const nextVolume = Number(value)
    if (!Number.isFinite(nextVolume)) return null
    return Math.max(0, Math.min(1, nextVolume))
}

const YOUTUBE_PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2]
const HOVER_VIDEO_CONTROLS_QUERY = '(hover: hover) and (pointer: fine)'

const canUseHoverVideoControls = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(HOVER_VIDEO_CONTROLS_QUERY).matches

// Tap/click can park focus on a control (notably Android taps); only
// keyboard-visible focus should pin the chrome open.
const matchesFocusVisible = element => {
    try {
        return element.matches(':focus-visible')
    } catch {
        return true
    }
}

const clampYouTubePlaybackRate = value => {
    const nextRate = Number(value)
    return Number.isFinite(nextRate) && nextRate > 0 ? nextRate : null
}

const normalizeYouTubePlaybackRates = (rates, currentRate = null) => {
    const baseRates = Array.isArray(rates) && rates.length ? rates : YOUTUBE_PLAYBACK_RATES
    const normalizedRates = baseRates
        .map(clampYouTubePlaybackRate)
        .filter(rate => rate != null)
        .filter(
            (rate, index, list) => list.findIndex(item => Math.abs(item - rate) < 0.001) === index,
        )
        .sort((left, right) => left - right)
    const normalizedCurrentRate = clampYouTubePlaybackRate(currentRate)

    if (
        normalizedCurrentRate != null &&
        !normalizedRates.some(rate => Math.abs(rate - normalizedCurrentRate) < 0.001)
    ) {
        normalizedRates.push(normalizedCurrentRate)
        normalizedRates.sort((left, right) => left - right)
    }

    return normalizedRates.length ? normalizedRates : YOUTUBE_PLAYBACK_RATES
}

class AnswerYouTubePlayer extends React.PureComponent {
    constructor(props) {
        super(props)
        this.mediaOwnerId = createMediaPlaybackOwnerId('youtube-player')
        this.autoplayOnReady = false
        this.transferFromDockOnReady = false
        this.playHereHandoffMediaKey = null
        this.pendingCaptionVideoId = null
        this.originRef = React.createRef()
        this.youtubeMediaMenuRef = React.createRef()
        this.dockUnsubscribe = null
        this.captionsUnsubscribe = null
        this.registeredMediaKey = null
        this.registeredOriginEl = null
        this.progressTimer = null
        this.hoverChromeMediaQuery = null
        this.syncHoverChromeMode = null
        this.panelChromeAutoHideTimeout = null
        this.panelChromeIntentActive = false
        // Hosted playback: this surface never owns a YouTube iframe. The media
        // dock's single runtime plays over this frame (its controls are
        // portalled in here) and glides to the corner when the frame leaves.
        this.hostedPlayback = hostedYouTubePlaybackEnabled()

        this.state = {
            dockOwned: false,
            hostedInline: false,
            panelReady: false,
            panelPlaying: false,
            panelCurrentTime: 0,
            panelDuration: 0,
            panelVolume: 1,
            panelPlaybackRate: 1,
            panelPlaybackRates: YOUTUBE_PLAYBACK_RATES,
            panelCaptionsAvailable: false,
            panelCaptionsEnabled: false,
            panelDragging: false,
            usesHoverChrome: canUseHoverVideoControls(),
            panelChromeIntentVisible: false,
            youtubeMediaMenuOpen: false,
        }
    }

    componentDidMount() {
        this.syncDockRegistration()
        this.initHoverChromeMode()
        if (typeof document !== 'undefined') {
            document.addEventListener('fullscreenchange', this.handleFullscreenChange)
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.videoId !== this.props.videoId) {
            const player = this.ytPlayer
            this.pendingCaptionVideoId = this.props.videoId
            this.captionsUnsubscribe?.()
            this.captionsUnsubscribe = null
            if (player) setYouTubeCaptionsEnabled(player, false)
            this.ytPlayer = null
            this.autoplayOnReady = false
            this.transferFromDockOnReady = false
            this.playHereHandoffMediaKey = null
            this.stopProgressTimer()
            this.setState({
                dockOwned: false,
                hostedInline: false,
                panelReady: false,
                panelPlaying: false,
                panelCurrentTime: 0,
                panelDuration: 0,
                panelVolume: 1,
                panelPlaybackRate: 1,
                panelPlaybackRates: YOUTUBE_PLAYBACK_RATES,
                panelCaptionsAvailable: false,
                panelCaptionsEnabled: false,
                panelDragging: false,
                panelChromeIntentVisible: false,
                youtubeMediaMenuOpen: false,
            })
        }

        if (
            prevState.youtubeMediaMenuOpen !== this.state.youtubeMediaMenuOpen &&
            typeof document !== 'undefined'
        ) {
            if (this.state.youtubeMediaMenuOpen) {
                document.addEventListener('pointerdown', this.handleDocumentPointerDown, true)
            } else {
                document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true)
            }
        }

        this.syncDockRegistration()
    }

    componentWillUnmount() {
        this.clearPanelChromeAutoHide()
        if (typeof document !== 'undefined') {
            document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
            document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true)
        }
        this.stopProgressTimer()
        this.teardownHoverChromeMode()
        this.dockUnsubscribe?.()
        this.dockUnsubscribe = null
        this.captionsUnsubscribe?.()
        this.captionsUnsubscribe = null
        this.clearRegisteredTrack()
        releaseMediaPlayback(this.mediaOwnerId)
        unregisterMediaPlaybackOwner(this.mediaOwnerId)
    }

    getYtPlayer = () => this.ytPlayer

    isLoaded = () => Boolean(this.props.videoId && this.props.isLoaded)

    getMediaKey = (videoId = this.props.videoId) => (videoId ? `youtube:${videoId}` : null)

    initializePendingCaptionVideo = player => {
        const videoId = this.pendingCaptionVideoId
        if (!videoId) return true

        if (typeof player?.getVideoData === 'function') {
            try {
                if (String(player.getVideoData()?.video_id || '') !== String(videoId)) return false
            } catch {
                return false
            }
        }

        this.pendingCaptionVideoId = null
        disableYouTubeCaptions(player, videoId)
        restoreYouTubeCaptions(player, this.getPlaybackState(videoId))
        return true
    }

    getMatchingBsvAttachment = () => {
        const attachmentId = this.props.answer?.url?.full_text_attachment_id
        if (!attachmentId) return null

        return (
            this.props.answer?.bsv_attachments?.find(
                attachment => attachment.attachment_id === attachmentId,
            ) || null
        )
    }

    handleDocumentPointerDown = event => {
        if (!this.state.youtubeMediaMenuOpen) return
        if (this.youtubeMediaMenuRef.current?.contains(event.target)) return
        this.setState({ youtubeMediaMenuOpen: false })
    }

    initHoverChromeMode = () => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return

        this.hoverChromeMediaQuery = window.matchMedia(HOVER_VIDEO_CONTROLS_QUERY)
        this.syncHoverChromeMode = () => {
            this.setState({
                usesHoverChrome: this.hoverChromeMediaQuery.matches,
                panelChromeIntentVisible: false,
            })
        }
        this.syncHoverChromeMode()
        this.hoverChromeMediaQuery.addEventListener?.('change', this.syncHoverChromeMode)
    }

    teardownHoverChromeMode = () => {
        this.hoverChromeMediaQuery?.removeEventListener?.('change', this.syncHoverChromeMode)
        this.hoverChromeMediaQuery = null
        this.syncHoverChromeMode = null
    }

    clearPanelChromeAutoHide = () => {
        if (!this.panelChromeAutoHideTimeout || typeof window === 'undefined') return
        window.clearTimeout(this.panelChromeAutoHideTimeout)
        this.panelChromeAutoHideTimeout = null
    }

    // The element fullscreen targets: the hosted runtime's surface while the
    // dock plays over this frame, otherwise this frame itself.
    getFullscreenTargetElement = () =>
        (this.state.hostedInline ? getMediaDockHostedSurfaceElement() : null) ||
        this.originRef.current

    isPanelFullscreenActive = () => {
        if (typeof document === 'undefined') return false
        const fullscreenElement = document.fullscreenElement
        const originElement = this.getFullscreenTargetElement()
        if (!fullscreenElement || !originElement) return false

        return (
            fullscreenElement === originElement ||
            fullscreenElement.contains(originElement) ||
            originElement.contains(fullscreenElement)
        )
    }

    schedulePanelChromeAutoHide = (delay = 1100) => {
        if (typeof window === 'undefined') return

        this.clearPanelChromeAutoHide()
        this.panelChromeAutoHideTimeout = window.setTimeout(() => {
            this.panelChromeAutoHideTimeout = null
            if (this.panelChromeIntentActive || this.state.panelDragging) return

            const activeElement = typeof document !== 'undefined' ? document.activeElement : null
            if (
                typeof Node !== 'undefined' &&
                activeElement instanceof Node &&
                (this.originRef.current?.contains(activeElement) ||
                    (this.state.hostedInline &&
                        getMediaDockHostedChromeLayer()?.contains(activeElement))) &&
                matchesFocusVisible(activeElement)
            ) {
                return
            }

            this.setState({ panelChromeIntentVisible: false })
        }, delay)
    }

    revealPanelChromeForPointerIntent = () => {
        if (!this.state.usesHoverChrome) return
        this.clearPanelChromeAutoHide()
        this.setState({ panelChromeIntentVisible: true }, () => {
            this.schedulePanelChromeAutoHide()
        })
    }

    handleFullscreenChange = () => {
        this.clearPanelChromeAutoHide()
        if (this.isPanelFullscreenActive()) {
            this.setState({ panelChromeIntentVisible: true }, () =>
                this.schedulePanelChromeAutoHide(1400),
            )
        }
    }

    handlePanelChromeMouseEnter = () => {
        this.revealPanelChromeForPointerIntent()
    }

    handlePanelChromeMouseMove = () => {
        this.revealPanelChromeForPointerIntent()
    }

    handlePanelChromeMouseLeave = event => {
        if (!this.state.usesHoverChrome) return
        if (this.state.panelDragging) return
        this.clearPanelChromeAutoHide()
        const activeElement = typeof document !== 'undefined' ? document.activeElement : null
        if (
            typeof Node !== 'undefined' &&
            activeElement instanceof Node &&
            event.currentTarget.contains(activeElement) &&
            matchesFocusVisible(activeElement)
        ) {
            return
        }
        if (this.isPanelFullscreenActive()) {
            this.schedulePanelChromeAutoHide(650)
        } else {
            this.setState({ panelChromeIntentVisible: false })
        }
    }

    handlePanelChromeFocus = event => {
        if (!this.state.usesHoverChrome) return
        this.clearPanelChromeAutoHide()
        this.setState({ panelChromeIntentVisible: true }, () => {
            // Window refocus re-fires focus on a pointer-focused control;
            // only keyboard-visible focus should hold the chrome open.
            if (!matchesFocusVisible(event.target)) this.schedulePanelChromeAutoHide()
        })
    }

    handlePanelChromeBlur = event => {
        if (!this.state.usesHoverChrome) return
        if (
            typeof Node !== 'undefined' &&
            event.relatedTarget instanceof Node &&
            event.currentTarget.contains(event.relatedTarget)
        ) {
            return
        }
        this.clearPanelChromeAutoHide()
        if (this.isPanelFullscreenActive()) {
            this.schedulePanelChromeAutoHide(500)
        } else {
            this.setState({ panelChromeIntentVisible: false })
        }
    }

    handlePanelChromeIntentStart = event => {
        if (!this.state.usesHoverChrome) return
        if (event?.type === 'focus' && !matchesFocusVisible(event.target)) {
            // Window refocus re-fires focus on a pointer-focused control —
            // show briefly, but don't hold the chrome open.
            this.clearPanelChromeAutoHide()
            this.setState({ panelChromeIntentVisible: true }, () =>
                this.schedulePanelChromeAutoHide(),
            )
            return
        }
        this.panelChromeIntentActive = true
        this.clearPanelChromeAutoHide()
        this.setState({ panelChromeIntentVisible: true })
    }

    handlePanelChromeIntentEnd = event => {
        if (!this.state.usesHoverChrome) return
        this.panelChromeIntentActive = false

        if (
            typeof Node !== 'undefined' &&
            event?.relatedTarget instanceof Node &&
            event.currentTarget.contains(event.relatedTarget)
        ) {
            return
        }

        this.schedulePanelChromeAutoHide(650)
    }

    isPanelChromeShown = () =>
        !this.state.panelPlaying || this.state.panelChromeIntentVisible || this.state.panelDragging

    handlePanelFullscreenPointerClick = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (this.state.usesHoverChrome) {
            const wasVisible = this.isPanelChromeShown()
            this.revealPanelChromeForPointerIntent()
            if (wasVisible) this.handlePanelTogglePlayback(ev)
            return
        }

        // Touch: tapping the video toggles the chrome (chrome taps stop
        // propagation before reaching this layer).
        this.clearPanelChromeAutoHide()
        if (this.state.panelChromeIntentVisible) {
            this.setState({ panelChromeIntentVisible: false })
        } else {
            this.setState({ panelChromeIntentVisible: true }, () =>
                this.schedulePanelChromeAutoHide(2600),
            )
        }
    }

    getTrackTitle = () => {
        const url = this.props.url
        return url?.title || url?.address || 'YouTube'
    }

    getSrc = (videoId = this.props.videoId) => {
        return this.props.url?.address || `https://www.youtube.com/watch?v=${videoId}`
    }

    getPosterSrc = (videoId = this.props.videoId) => {
        return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null
    }

    requestLoaded = () => {
        const { videoId, onLoadedChange } = this.props
        if (!videoId) return
        onLoadedChange?.(videoId, true)
    }

    loadAndPlay = () => {
        const { videoId } = this.props
        if (!videoId) return

        if (this.hostedPlayback) {
            this.requestHostedPlayback('inline')
            return
        }

        this.autoplayOnReady = true
        this.transferFromDockOnReady = true
        this.requestLoaded()
    }

    // Ask the dock to play this video with its own runtime parked over this
    // frame. The track is (re)registered first so the dock can resolve it.
    requestHostedPlayback = (source = 'inline') => {
        const mediaKey = this.getMediaKey()
        if (!mediaKey) return false

        this.syncDockRegistration()
        if (!this.getHostedOwner()) {
            setMediaPlaybackState(mediaKey, { currentTime: this.getLoadTime(null) })
        }
        const requested = requestHostedMediaPlayback(mediaKey, {
            originEl: this.originRef.current,
            source,
        })
        if (requested) {
            this.clearPanelChromeAutoHide()
            this.setState({ panelChromeIntentVisible: true }, () =>
                this.schedulePanelChromeAutoHide(2000),
            )
        }
        return requested
    }

    // Hosted chrome reads the dock runtime instead of a local player.
    getHostedOwner = () => {
        const mediaKey = this.getMediaKey()
        return mediaKey ? findMountedOwnerForMediaKey(mediaKey) : null
    }

    // Reads the hosted runtime instead of a local player. Returns a patch only
    // when something actually changed: this runs on a poll and on every dock
    // event, and componentDidUpdate re-enters the dock sync, so an
    // unconditional setState here would never settle.
    updateHostedPanelProgress = () => {
        const owner = this.getHostedOwner()
        if (!owner) {
            this.setState(previousState =>
                previousState.panelReady || previousState.panelPlaying
                    ? { panelReady: false, panelPlaying: false }
                    : null,
            )
            return
        }

        const snapshot = getMediaOwnerPlaybackSnapshot(owner)
        const control = getMediaOwnerControlSnapshot(owner)
        this.setState(previousState => {
            const playbackRates = normalizeYouTubePlaybackRates(
                control?.playbackRates,
                control?.playbackRate ?? previousState.panelPlaybackRate,
            )
            const ratesChanged =
                playbackRates.length !== previousState.panelPlaybackRates?.length ||
                playbackRates.some(
                    (rate, index) => rate !== previousState.panelPlaybackRates?.[index],
                )
            const next = {
                panelReady: true,
                panelPlaying: Boolean(control && !control.isPaused && !control.isEnded),
                ...(previousState.panelDragging || !Number.isFinite(snapshot.currentTime)
                    ? {}
                    : { panelCurrentTime: snapshot.currentTime }),
                ...(snapshot.duration > 0 ? { panelDuration: snapshot.duration } : {}),
                ...(control?.volume == null ? {} : { panelVolume: control.volume }),
                ...(control?.playbackRate == null
                    ? {}
                    : { panelPlaybackRate: control.playbackRate }),
                ...(ratesChanged ? { panelPlaybackRates: playbackRates } : {}),
                panelCaptionsAvailable: control?.captionsAvailable === true,
                panelCaptionsEnabled: control?.captionsEnabled === true,
            }

            const changed = Object.keys(next).some(key => previousState[key] !== next[key])
            return changed ? next : null
        })
    }

    getResetTime = player => {
        try {
            return this.getStartTime(player?.getDuration?.())
        } catch {
            return 0
        }
    }

    getStartTime = duration => {
        const vidStart = this.props.vidStart
        if (
            vidStart &&
            0 <= parseInt(vidStart, 10) &&
            (!Number.isFinite(duration) || parseInt(vidStart, 10) < duration)
        ) {
            return parseInt(vidStart, 10)
        }

        return 0
    }

    getPlaybackState = (videoId = this.props.videoId) => {
        const mediaKey = this.getMediaKey(videoId)
        return mediaKey ? getMediaPlaybackState(mediaKey) : null
    }

    getLoadTime = (player, videoId = this.props.videoId) => {
        const stored = this.getPlaybackState(videoId)
        const storedTime = stored?.currentTime
        let duration = Number.isFinite(stored?.duration) ? stored.duration : 0
        if (!duration) {
            try {
                duration = player?.getDuration?.() || 0
            } catch {}
        }

        if (
            Number.isFinite(storedTime) &&
            storedTime > 0.05 &&
            (!duration || storedTime < duration - 0.25)
        ) {
            return storedTime
        }
        return this.getResetTime(player)
    }

    getIframe = player => {
        try {
            return player?.getIframe?.() || null
        } catch {
            return null
        }
    }

    isOriginVisible = () => isElementUserVisible(this.originRef.current)

    getPlayerState = (player = this.ytPlayer) => {
        try {
            return player?.getPlayerState?.()
        } catch {
            return null
        }
    }

    getPanelVolume = (player = this.ytPlayer) => {
        try {
            const playerVolume = player?.getVolume?.()
            if (Number.isFinite(playerVolume)) return clampYouTubeVolume(playerVolume / 100)
        } catch {}

        return null
    }

    getPanelPlaybackRate = (player = this.ytPlayer) => {
        try {
            return clampYouTubePlaybackRate(player?.getPlaybackRate?.())
        } catch {
            return null
        }
    }

    getPanelPlaybackRates = (player = this.ytPlayer, currentRate = null) => {
        try {
            return normalizeYouTubePlaybackRates(player?.getAvailablePlaybackRates?.(), currentRate)
        } catch {
            return normalizeYouTubePlaybackRates(YOUTUBE_PLAYBACK_RATES, currentRate)
        }
    }

    updateDockOwnedState = () => {
        const mediaKey = this.getMediaKey()
        const dockOwned = Boolean(mediaKey && isMediaKeyPresentedInDock(mediaKey))
        const hostedInline = Boolean(
            this.hostedPlayback &&
                mediaKey &&
                isMediaOriginHostedInline(mediaKey, this.originRef.current),
        )

        if (this.state.dockOwned !== dockOwned || this.state.hostedInline !== hostedInline) {
            this.setState({ dockOwned, hostedInline }, () => {
                this.syncProgressTimer()
                if (this.state.hostedInline) this.updatePanelProgress()
            })
            return
        }

        // The placement is unchanged, but the runtime it hosts may have started
        // or stopped, so the poll has to be re-synced. This path runs inside
        // componentDidUpdate — it must never setState (syncProgressTimer only
        // starts or stops the interval); the chrome is refreshed from the dock
        // event instead.
        if (hostedInline) this.syncProgressTimer()
    }

    handleMediaDockEvent = event => {
        const mediaKey = this.getMediaKey()
        if (
            event?.type === 'inlineHandoffPending' &&
            event.mediaKey === mediaKey &&
            String(event.answerId || '') === String(this.props.answer?.id || '') &&
            !this.props.isLoaded
        ) {
            this.requestLoaded()
        }
        // The dock is leaving cinema mode with this video, which it took from
        // this post only to show it there — or its hosted runtime just left
        // another post showing this video as the user navigated here: bring
        // playback back here, exactly as "Play here" does, while this post is
        // the one on screen.
        if (
            (event?.type === 'cinemaExit' || event?.type === 'hostedReturn') &&
            event.mediaKey === mediaKey &&
            event.originEl === this.originRef.current &&
            this.isOriginVisible()
        ) {
            this.handlePlayHere()
        }
        this.updateDockOwnedState()
        // The open menu's playlist row reads membership as it renders; a
        // playlist change (here, in the dock, or a server sync) must redraw it
        // so the row never offers the opposite of what a press would do.
        if (event?.type === 'playlist' && this.state.youtubeMediaMenuOpen) {
            this.forceUpdate()
        }
        // The dock's chrome layer (this surface's portal target) mounted or
        // unmounted: re-render so the controls move with it.
        if (event?.type === 'hostedChromeLayer' && this.state.hostedInline) {
            this.forceUpdate()
        }
        // A hosted runtime started or stopped: refresh the controls from it.
        // Safe here (an event callback, not the React update cycle), and
        // updateHostedPanelProgress is a no-op when nothing changed.
        if (
            this.hostedPlayback &&
            ['claim', 'release', 'focus', 'controls'].includes(event?.type) &&
            mediaKey &&
            isMediaOriginHostedInline(mediaKey, this.originRef.current)
        ) {
            this.updatePanelProgress()
        }
    }

    clearRegisteredTrack = () => {
        if (this.registeredMediaKey && this.registeredOriginEl) {
            clearMediaTrackOrigin(this.registeredMediaKey, this.registeredOriginEl)
        }
        this.registeredMediaKey = null
        this.registeredOriginEl = null
    }

    syncDockRegistration = () => {
        const videoId = this.props.videoId
        const mediaKey = this.getMediaKey(videoId)

        if (!mediaKey) {
            this.clearRegisteredTrack()
            this.dockUnsubscribe?.()
            this.dockUnsubscribe = null
            this.updateDockOwnedState()
            return
        }

        if (!this.dockUnsubscribe) {
            this.dockUnsubscribe = subscribeToMediaDock(this.handleMediaDockEvent)
        }

        registerMediaTrack(mediaKey, {
            kind: 'youtube',
            src: this.getSrc(videoId),
            videoId,
            posterSrc: this.getPosterSrc(videoId),
            title: this.getTrackTitle(),
            answerId: this.props.answer?.id || null,
            questId: this.props.answer?.quest_id || null,
        })

        if (this.registeredMediaKey && this.registeredMediaKey !== mediaKey) {
            this.clearRegisteredTrack()
        }

        const originEl = this.originRef.current
        if (!originEl && this.registeredOriginEl) {
            clearMediaTrackOrigin(mediaKey, this.registeredOriginEl)
            this.registeredOriginEl = null
        } else if (originEl && this.registeredOriginEl !== originEl) {
            if (this.registeredMediaKey && this.registeredOriginEl) {
                clearMediaTrackOrigin(this.registeredMediaKey, this.registeredOriginEl)
            }
            setMediaTrackOrigin(mediaKey, originEl)
            this.registeredOriginEl = originEl
        }
        this.registeredMediaKey = mediaKey
        this.updateDockOwnedState()
    }

    persistPlaybackState = (player = this.ytPlayer, overrides = {}) => {
        const mediaKey = this.getMediaKey()
        if (!mediaKey) return null

        let currentTime = null
        try {
            currentTime = player?.getCurrentTime?.()
        } catch {}
        let duration = null
        try {
            duration = player?.getDuration?.()
        } catch {}
        let playbackRate = null
        try {
            playbackRate = player?.getPlaybackRate?.()
        } catch {}

        return setMediaPlaybackState(mediaKey, {
            ...(Number.isFinite(duration) && duration > 0 ? { duration } : {}),
            ...(Number.isFinite(currentTime) && currentTime >= 0 ? { currentTime } : {}),
            ...(clampYouTubePlaybackRate(playbackRate) != null
                ? { playbackRate: clampYouTubePlaybackRate(playbackRate) }
                : {}),
            ...overrides,
        })
    }

    syncPlaybackOwner = (player = this.ytPlayer, videoId = this.props.videoId) => {
        if (!player) return

        syncMediaPlaybackOwner(
            this.mediaOwnerId,
            this.getMediaKey(videoId),
            this.getIframe(player),
            {
                play: () => player.playVideo?.(),
                pause: () => {
                    this.persistPlaybackState(player)
                    player.pauseVideo?.()
                },
                isPaused: () => {
                    const state = player.getPlayerState?.()
                    return state !== 1 && state !== 3
                },
                isVideo: () => true,
                resetToStart: () => player.seekTo?.(this.getResetTime(player), true),
                seekTo: nextTime => player.seekTo?.(nextTime, true),
                setVolume: volume => player.setVolume?.(Math.round(volume * 100)),
                getCurrentTime: () => player.getCurrentTime?.(),
                getDuration: () => player.getDuration?.(),
                isEnded: () => player.getPlayerState?.() === 0,
                getCaptionsState: () => readYouTubeCaptionsState(player),
                setCaptionsEnabled: enabled => setYouTubeCaptionsEnabled(player, enabled),
            },
        )
    }

    updatePanelProgress = (player = this.ytPlayer) => {
        if (!player && this.hostedPlayback) {
            this.updateHostedPanelProgress()
            return
        }
        if (!player) return

        let currentTime = null
        let duration = null
        try {
            currentTime = player.getCurrentTime?.()
        } catch {}
        try {
            duration = player.getDuration?.()
        } catch {}

        const playerState = this.getPlayerState(player)
        const playerVolume = this.getPanelVolume(player)
        const playerPlaybackRate = this.getPanelPlaybackRate(player)
        const playerPlaybackRates = this.getPanelPlaybackRates(player, playerPlaybackRate)
        const captions = readYouTubeCaptionsState(player)

        this.setState(previousState => ({
            panelReady: true,
            panelPlaying: playerState === 1 || playerState === 3,
            ...(previousState.panelDragging || !Number.isFinite(currentTime) || currentTime < 0
                ? {}
                : { panelCurrentTime: currentTime }),
            ...(Number.isFinite(duration) && duration > 0 ? { panelDuration: duration } : {}),
            ...(playerVolume == null ? {} : { panelVolume: playerVolume }),
            ...(playerPlaybackRate == null ? {} : { panelPlaybackRate: playerPlaybackRate }),
            panelPlaybackRates: playerPlaybackRates,
            panelCaptionsAvailable: captions.available,
            panelCaptionsEnabled: captions.enabled,
        }))
    }

    startProgressTimer = () => {
        this.stopProgressTimer()
        if (typeof window === 'undefined') return

        this.progressTimer = window.setInterval(() => {
            this.updatePanelProgress()
        }, 250)
    }

    stopProgressTimer = () => {
        if (!this.progressTimer || typeof window === 'undefined') return
        window.clearInterval(this.progressTimer)
        this.progressTimer = null
    }

    subscribeToCaptionState = player => {
        this.captionsUnsubscribe?.()
        this.captionsUnsubscribe = subscribeToYouTubeCaptionsState(player, captions => {
            if (player !== this.ytPlayer) return
            this.setState({
                panelCaptionsAvailable: captions.available,
                panelCaptionsEnabled: captions.enabled,
            })
            const mediaKey = this.getMediaKey()
            if (captions.enabled && getMediaPlaybackState(mediaKey)?.captions !== true) {
                setMediaPlaybackState(mediaKey, { captions: true })
            }
            this.syncPlaybackOwner(player)
        })
    }

    syncProgressTimer = (player = this.ytPlayer) => {
        // Hosted chrome has no player events to follow, so it polls the dock
        // runtime — but only while that runtime is actually advancing. A paused
        // video parked over this frame must not re-render the chrome forever.
        if (!player && this.hostedPlayback) {
            const owner = this.state.hostedInline ? this.getHostedOwner() : null
            const control = owner ? getMediaOwnerControlSnapshot(owner) : null
            if (control && !control.isPaused && !control.isEnded) {
                if (!this.progressTimer) this.startProgressTimer()
            } else {
                this.stopProgressTimer()
            }
            return
        }

        const playerState = this.getPlayerState(player)
        if (playerState === 1 || playerState === 3) {
            if (!this.progressTimer) this.startProgressTimer()
            return
        }

        this.stopProgressTimer()
    }

    syncPanelVolumeFromPlaybackState = player => {
        const storedVolume = clampYouTubeVolume(this.getPlaybackState()?.volume)
        const playerVolume = this.getPanelVolume(player)
        const nextVolume = storedVolume ?? playerVolume
        if (nextVolume == null) return

        try {
            player?.setVolume?.(Math.round(nextVolume * 100))
        } catch {}

        this.setState({ panelVolume: nextVolume })
    }

    syncPanelPlaybackRateFromPlaybackState = player => {
        const storedPlaybackRate = clampYouTubePlaybackRate(this.getPlaybackState()?.playbackRate)
        const playerPlaybackRate = this.getPanelPlaybackRate(player)
        const playbackRates = this.getPanelPlaybackRates(
            player,
            storedPlaybackRate ?? playerPlaybackRate,
        )
        const storedRateIsAvailable =
            storedPlaybackRate != null &&
            playbackRates.some(rate => Math.abs(rate - storedPlaybackRate) < 0.001)
        const nextPlaybackRate =
            (storedRateIsAvailable ? storedPlaybackRate : null) ?? playerPlaybackRate ?? 1

        try {
            player?.setPlaybackRate?.(nextPlaybackRate)
        } catch {}

        this.setState({
            panelPlaybackRate: nextPlaybackRate,
            panelPlaybackRates: playbackRates,
        })
    }

    claimPlayback = ({ transferFromDock = null } = {}) => {
        const mediaKey = this.getMediaKey()
        return claimMediaPlayback(this.mediaOwnerId, {
            transferFromDock:
                transferFromDock == null ? isMediaKeyPlayingInDock(mediaKey) : transferFromDock,
        })
    }

    handleTogglePlaylist = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        const mediaKey = this.getMediaKey()
        if (!mediaKey) return
        toggleTrackInPlaylist(mediaKey, 'youtube_corner_button')
        this.setState({ youtubeMediaMenuOpen: false })
    }

    handlePlayInDock = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        const mediaKey = this.getMediaKey()
        if (!mediaKey) return
        playTrackInDock(mediaKey, 'youtube_corner_button')
        this.setState({ youtubeMediaMenuOpen: false })
    }

    handleOpenCinema = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        const mediaKey = this.getMediaKey()
        if (!mediaKey) return
        openTrackInCinema(mediaKey, 'youtube_corner_button', {
            originEl: this.originRef.current,
            returnFocusEl: this.youtubeMediaMenuRef.current?.querySelector(
                'button[aria-haspopup="menu"]',
            ),
        })
        this.setState({ youtubeMediaMenuOpen: false })
    }

    handlePanelTogglePlayback = ev => {
        ev?.preventDefault?.()
        ev?.stopPropagation?.()

        if (this.hostedPlayback) {
            const mediaKey = this.getMediaKey()
            const owner = this.getHostedOwner()
            const control = owner ? getMediaOwnerControlSnapshot(owner) : null
            if (control && !control.isPaused && !control.isEnded) {
                pauseMediaKey(mediaKey)
            } else if (!owner || !playMediaKey(mediaKey)) {
                this.requestHostedPlayback('inline_chrome')
            }
            this.updatePanelProgress()
            return
        }

        if (!this.ytPlayer) {
            this.loadAndPlay()
            return
        }

        const playerState = this.getPlayerState()
        const isPlaying = playerState === 1 || playerState === 3

        if (isPlaying) {
            this.persistPlaybackState(this.ytPlayer)
            this.ytPlayer.pauseVideo?.()
            this.updatePanelProgress()
            return
        }

        this.syncPanelVolumeFromPlaybackState(this.ytPlayer)
        this.syncPanelPlaybackRateFromPlaybackState(this.ytPlayer)
        this.syncPlaybackOwner(this.ytPlayer)
        const didPlay = playMediaPlaybackOwner(this.mediaOwnerId, {
            transferFromDock: true,
        })
        if (!didPlay) {
            const didClaim = this.claimPlayback({ transferFromDock: true })
            if (!didClaim) return
            this.ytPlayer.playVideo?.()
        }
        this.updatePanelProgress()
    }

    handlePanelSeek = nextTime => {
        const mediaKey = this.getMediaKey()
        if (!mediaKey || !Number.isFinite(nextTime)) return

        this.setState({ panelCurrentTime: nextTime })
        if (this.hostedPlayback) {
            seekMediaKey(mediaKey, nextTime)
            return
        }
        seekMediaKey(mediaKey, nextTime, {
            ownerId: this.mediaOwnerId,
            transferFromDock: true,
        })
    }

    handlePanelSeekStart = ev => {
        ev?.stopPropagation?.()
        this.setState({ panelDragging: true })
    }

    handlePanelSeekEnd = ev => {
        ev?.stopPropagation?.()
        this.setState({ panelDragging: false }, () => this.updatePanelProgress())
    }

    handlePanelVolume = nextVolume => {
        const mediaKey = this.getMediaKey()
        const volume = clampYouTubeVolume(nextVolume)
        if (!mediaKey || volume == null) return

        this.setState({ panelVolume: volume })
        if (this.hostedPlayback) {
            setMediaKeyVolume(mediaKey, volume)
            return
        }
        setMediaKeyVolume(mediaKey, volume, {
            ownerId: this.mediaOwnerId,
            transferFromDock: true,
        })
    }

    handlePanelPlaybackRate = nextPlaybackRate => {
        const mediaKey = this.getMediaKey()
        const playbackRate = clampYouTubePlaybackRate(nextPlaybackRate)
        if (!mediaKey || playbackRate == null) return

        this.setState({ panelPlaybackRate: playbackRate })

        if (this.hostedPlayback) {
            setMediaKeyPlaybackRate(mediaKey, playbackRate)
            return
        }

        try {
            this.ytPlayer?.setPlaybackRate?.(playbackRate)
        } catch {}

        setMediaPlaybackState(mediaKey, { playbackRate })
    }

    handlePanelToggleCaptions = () => {
        const mediaKey = this.getMediaKey()
        const enabled = !this.state.panelCaptionsEnabled
        if (!mediaKey) return

        if (this.hostedPlayback) {
            if (!setMediaKeyCaptionsEnabled(mediaKey, enabled)) {
                this.updateHostedPanelProgress()
                return
            }
            if (!enabled) this.setState({ panelCaptionsEnabled: false })
            return
        }

        if (!setYouTubeCaptionsEnabled(this.ytPlayer, enabled)) {
            const captions = readYouTubeCaptionsState(this.ytPlayer)
            this.setState({
                panelCaptionsAvailable: captions.available,
                panelCaptionsEnabled: captions.enabled,
            })
            return
        }

        this.setState({ panelCaptionsEnabled: enabled })
        if (!enabled) setMediaPlaybackState(mediaKey, { captions: false })
    }

    handlePanelFullscreen = ev => {
        ev?.preventDefault?.()
        ev?.stopPropagation?.()
        ev?.currentTarget?.blur?.()
        this.getFullscreenTargetElement()
            ?.requestFullscreen?.()
            .catch?.(() => {})
    }

    handleReady = ev => {
        const player = (this.ytPlayer = ev.target)
        this.pendingCaptionVideoId = this.props.videoId
        this.subscribeToCaptionState(player)
        if (!this.initializePendingCaptionVideo(player)) return
        this.syncPlaybackOwner(player)
        this.syncPanelVolumeFromPlaybackState(player)
        this.syncPanelPlaybackRateFromPlaybackState(player)
        this.updatePanelProgress(player)
        this.syncProgressTimer(player)

        const mediaKey = this.getMediaKey()
        if (this.playHereHandoffMediaKey === mediaKey) {
            this.playHereHandoffMediaKey = null
            if (isInlineMediaPlaybackHandoffPendingForAnswer(this.props.answer?.id, mediaKey)) {
                resumePendingInlineMediaPlayback(mediaKey, {
                    scopeElement: this.originRef.current,
                })
            }
            this.autoplayOnReady = false
            this.transferFromDockOnReady = false
            this.updatePanelProgress(player)
            this.updateDockOwnedState()
            return
        }

        const loadTime = this.getLoadTime(player)

        if (this.autoplayOnReady) {
            const canPlayInline = this.isOriginVisible()

            if (canPlayInline) {
                const didClaim = this.claimPlayback({
                    transferFromDock: this.transferFromDockOnReady || canPlayInline,
                })
                if (!didClaim) {
                    this.autoplayOnReady = false
                    this.transferFromDockOnReady = false
                    this.updatePanelProgress(player)
                    this.updateDockOwnedState()
                    return
                }

                player.seekTo(loadTime, true)
                player.playVideo()
            } else {
                this.persistPlaybackState(player, { currentTime: loadTime })
                const didClaim = this.claimPlayback({ transferFromDock: false })
                if (didClaim) releaseMediaPlayback(this.mediaOwnerId)
            }
        } else {
            player.seekTo(loadTime, true)
        }

        this.autoplayOnReady = false
        this.transferFromDockOnReady = false
        this.updatePanelProgress(player)
        this.updateDockOwnedState()
    }

    handleStateChange = ev => {
        this.ytPlayer = ev.target
        if (!this.initializePendingCaptionVideo(ev.target)) return
        this.syncPlaybackOwner(ev.target)
        this.updatePanelProgress(ev.target)
        this.syncProgressTimer(ev.target)

        if (ev.data === 1) {
            this.persistPlaybackState(ev.target, { hasStartedPlaying: true })
            // As a handoff source this player deliberately keeps sounding
            // (usually offscreen) until the destination commits; neither the
            // offscreen self-pause nor a claim echo may disturb that window.
            if (
                !shouldClaimInlineYouTubePlayback(ev.data, {
                    isHandoffSource: isMediaPlaybackHandoffSourceOwner(this.mediaOwnerId),
                    isHandoffDestination: isInlineMediaPlaybackHandoffPendingForAnswer(
                        this.props.answer?.id,
                        this.getMediaKey(),
                    ),
                })
            ) {
                return
            }
            if (!this.isOriginVisible()) {
                ev.target?.pauseVideo?.()
                releaseMediaPlayback(this.mediaOwnerId)
                return
            }
            this.claimPlayback({ transferFromDock: true })
            this.setState({ panelChromeIntentVisible: true }, () =>
                this.schedulePanelChromeAutoHide(2000),
            )
            return
        }

        if (ev.data === 3) {
            this.persistPlaybackState(ev.target)
            if (
                !shouldClaimInlineYouTubePlayback(ev.data, {
                    isHandoffSource: isMediaPlaybackHandoffSourceOwner(this.mediaOwnerId),
                    isHandoffDestination: isInlineMediaPlaybackHandoffPendingForAnswer(
                        this.props.answer?.id,
                        this.getMediaKey(),
                    ),
                })
            ) {
                return
            }
            if (!this.isOriginVisible()) {
                ev.target?.pauseVideo?.()
                releaseMediaPlayback(this.mediaOwnerId)
                return
            }
            this.claimPlayback({ transferFromDock: true })
            return
        }

        if (ev.data === 0) {
            this.persistPlaybackState(ev.target, { currentTime: 0 })
            releaseMediaPlayback(this.mediaOwnerId, {
                ended: true,
                mediaKey: this.getMediaKey(),
            })
            return
        }

        if (ev.data === 2) {
            this.persistPlaybackState(ev.target)
            releaseMediaPlayback(this.mediaOwnerId)
            return
        }

        if (ev.data === 5 || ev.data === -1) {
            this.persistPlaybackState(ev.target)
        }
    }

    handlePlaybackRateChange = ev => {
        this.ytPlayer = ev?.target || this.ytPlayer
        const playbackRate = this.getPanelPlaybackRate(this.ytPlayer)
        const playbackRates = this.getPanelPlaybackRates(this.ytPlayer, playbackRate)

        this.setState({
            ...(playbackRate == null ? {} : { panelPlaybackRate: playbackRate }),
            panelPlaybackRates: playbackRates,
        })

        const mediaKey = this.getMediaKey()
        if (mediaKey && playbackRate != null) setMediaPlaybackState(mediaKey, { playbackRate })
    }

    handlePause = ev => {
        this.persistPlaybackState(ev?.target)
        this.updatePanelProgress(ev?.target)
        this.stopProgressTimer()
        releaseMediaPlayback(this.mediaOwnerId)
    }

    handleEnd = ev => {
        this.persistPlaybackState(ev?.target, { currentTime: 0 })
        this.updatePanelProgress(ev?.target)
        this.stopProgressTimer()
        releaseMediaPlayback(this.mediaOwnerId, {
            ended: true,
            mediaKey: this.getMediaKey(),
        })
    }

    handlePlayHere = ev => {
        ev?.preventDefault?.()
        ev?.stopPropagation?.()

        const videoId = this.props.videoId
        const answerId = this.props.answer?.id
        if (!videoId || !answerId) return

        // Hosted: the dock's runtime simply glides back over this frame.
        if (this.hostedPlayback) {
            this.requestHostedPlayback('play_here')
            return
        }

        // The handoff event can load multiple mounted copies of the same post.
        // Mark this instance before emitting it so only the clicked copy may
        // resume from its onReady callback.
        this.playHereHandoffMediaKey = this.getMediaKey(videoId)
        const mediaKey = prepareActiveMediaPlaybackForInlineHandoff(answerId)
        if (!mediaKey) {
            this.playHereHandoffMediaKey = null
            // A retained paused/completed dock has no active handoff source.
            // The placeholder still offers playback, so start its inline owner.
            this.handlePanelTogglePlayback()
            return
        }

        this.autoplayOnReady = false
        this.transferFromDockOnReady = false
        this.requestLoaded()

        if (!this.ytPlayer) return

        this.syncPlaybackOwner(this.ytPlayer)
        resumePendingInlineMediaPlayback(mediaKey, {
            scopeElement: this.originRef.current,
        })
        this.playHereHandoffMediaKey = null
    }

    renderMediaMenu = () => {
        const mediaKey = this.getMediaKey()
        // Read as the menu renders: opening re-renders, and so does a playlist
        // change while it is open (handleMediaDockEvent).
        const inPlaylist = Boolean(mediaKey) && isMediaTrackInPlaylist(mediaKey)
        const matchingBsvAttachment = this.getMatchingBsvAttachment()
        // A trailing YouTube link is dropped from the post text in favour of
        // this embed, so the menu's first row is the way out to youtube.com.
        const youtubeHref = safeHttpHref(this.getSrc())
        if (!mediaKey && !matchingBsvAttachment && !youtubeHref) return null

        const bsvTx = matchingBsvAttachment?.bsv_tx
        const txId = bsvTx?.tx_id || null
        const onchainStatusIcon = matchingBsvAttachment ? (
            <BsvTransactionStatusIcon
                bsvTx={bsvTx}
                fallbackTxId={txId}
                className={styles.youtubeMediaMenuStatusIcon}
                disableLink
            />
        ) : null

        return (
            <div className={styles.youtubeMediaMenu} ref={this.youtubeMediaMenuRef}>
                <button
                    type="button"
                    className={styles.youtubeMediaMenuButton}
                    aria-label="YouTube video actions"
                    aria-haspopup="menu"
                    aria-expanded={this.state.youtubeMediaMenuOpen}
                    title="YouTube video actions"
                    onClick={event => {
                        event.preventDefault()
                        event.stopPropagation()
                        this.setState(state => ({
                            youtubeMediaMenuOpen: !state.youtubeMediaMenuOpen,
                        }))
                    }}
                >
                    <Film size={16} strokeWidth={2.25} aria-hidden="true" />
                </button>
                {this.state.youtubeMediaMenuOpen && (
                    <div className={styles.youtubeMediaMenuList} role="menu">
                        {youtubeHref && (
                            <a
                                className={styles.youtubeMediaMenuItem}
                                href={youtubeHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                role="menuitem"
                                onClick={event => {
                                    event.stopPropagation()
                                    this.setState({ youtubeMediaMenuOpen: false })
                                }}
                            >
                                <ArrowUpRight size={14} strokeWidth={2.2} aria-hidden="true" />
                                <span>YouTube</span>
                            </a>
                        )}
                        {matchingBsvAttachment &&
                            (txId ? (
                                <a
                                    className={styles.youtubeMediaMenuItem}
                                    href={bsvTxExplorerUrl(txId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    role="menuitem"
                                    onClick={event => {
                                        event.stopPropagation()
                                        this.setState({ youtubeMediaMenuOpen: false })
                                    }}
                                >
                                    {onchainStatusIcon}
                                    <span>Onchain</span>
                                </a>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.youtubeMediaMenuItem}
                                    role="menuitem"
                                    onClick={event => {
                                        event.preventDefault()
                                        event.stopPropagation()
                                        this.setState({ youtubeMediaMenuOpen: false })
                                    }}
                                >
                                    {onchainStatusIcon}
                                    <span>Onchain</span>
                                </button>
                            ))}
                        {mediaKey && mediaDockCinemaEnabled() && (
                            <button
                                type="button"
                                className={styles.youtubeMediaMenuItem}
                                role="menuitem"
                                onClick={this.handleOpenCinema}
                            >
                                <TvMinimalPlayIcon size={14} strokeWidth={2.2} aria-hidden="true" />
                                <span>Cinema</span>
                            </button>
                        )}
                        {mediaKey && (
                            <button
                                type="button"
                                className={styles.youtubeMediaMenuItem}
                                role="menuitem"
                                onClick={this.handleTogglePlaylist}
                            >
                                {inPlaylist ? (
                                    <Check size={14} strokeWidth={2.2} aria-hidden="true" />
                                ) : (
                                    <ListMusic size={14} strokeWidth={2.2} aria-hidden="true" />
                                )}
                                <span>{playlistMenuLabel(inPlaylist)}</span>
                            </button>
                        )}
                        {mediaKey && (
                            <button
                                type="button"
                                className={styles.youtubeMediaMenuItem}
                                role="menuitem"
                                onClick={this.handlePlayInDock}
                            >
                                <PictureInPicture2 size={14} strokeWidth={2.2} aria-hidden="true" />
                                <span>{PLAY_IN_DOCK_LABEL}</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        )
    }

    // While the dock's runtime is parked over this frame, the controls render
    // into the dock's chrome layer so they sit above the video. Until that
    // layer mounts they render in place (briefly covered by the surface).
    renderHostedChrome = () => {
        const chrome = (
            <>
                <div className={dockStyles.hostedChromeItem}>{this.renderMediaMenu()}</div>
                <div className={dockStyles.hostedChromeItem}>{this.renderPanelControls()}</div>
            </>
        )
        const layer = getMediaDockHostedChromeLayer()
        return layer ? createPortal(chrome, layer) : chrome
    }

    renderPanelControls = () => {
        if (this.state.dockOwned && !this.state.hostedInline) return null
        if (!this.state.hostedInline && !this.state.panelReady && !this.ytPlayer) return null

        const duration = Number.isFinite(this.state.panelDuration)
            ? Math.max(0, this.state.panelDuration)
            : 0
        const currentTime = Math.min(
            Number.isFinite(this.state.panelCurrentTime)
                ? Math.max(0, this.state.panelCurrentTime)
                : 0,
            duration || this.state.panelCurrentTime || 0,
        )
        const volume = clampYouTubeVolume(this.state.panelVolume) ?? 1
        const playbackRate = clampYouTubePlaybackRate(this.state.panelPlaybackRate) ?? 1
        const playbackRates = normalizeYouTubePlaybackRates(
            this.state.panelPlaybackRates,
            playbackRate,
        )

        return (
            <VideoChrome
                variant="overlay"
                isPlaying={this.state.panelPlaying}
                currentTime={currentTime}
                duration={duration}
                volume={volume}
                playbackRate={playbackRate}
                playbackRates={playbackRates}
                captionsAvailable={this.state.panelCaptionsAvailable}
                captionsEnabled={this.state.panelCaptionsEnabled}
                visible={this.isPanelChromeShown()}
                onTogglePlayback={this.handlePanelTogglePlayback}
                onSeek={this.handlePanelSeek}
                onSeekStart={this.handlePanelSeekStart}
                onSeekEnd={this.handlePanelSeekEnd}
                onVolumeChange={this.handlePanelVolume}
                onPlaybackRateChange={this.handlePanelPlaybackRate}
                onToggleCaptions={this.handlePanelToggleCaptions}
                onFullscreen={this.handlePanelFullscreen}
                onChromeIntentStart={this.handlePanelChromeIntentStart}
                onChromeIntentEnd={this.handlePanelChromeIntentEnd}
                dataTestId="youtube-panel-video-chrome"
            />
        )
    }

    renderDockOverlay = () => {
        if (!this.state.dockOwned || this.state.hostedInline) return null

        return (
            <div
                className={styles.youtubePlayingInDockOverlay}
                data-dock-placeholder=""
                onClick={ev => ev.stopPropagation()}
                onMouseDown={ev => ev.stopPropagation()}
                onPointerDown={ev => ev.stopPropagation()}
                onTouchStart={ev => ev.stopPropagation()}
            >
                <span className={styles.youtubePlayingInDockLabel}>Playing in dock</span>
                <button
                    type="button"
                    className={styles.youtubePlayHereButton}
                    onClick={this.handlePlayHere}
                    aria-label="Play YouTube video here"
                >
                    <Play size={14} strokeWidth={2.25} fill="currentColor" aria-hidden="true" />
                    Play here
                </button>
            </div>
        )
    }

    render() {
        const { answer, containerClassName, isLoaded, isReplyPreview, url, videoId } = this.props
        if (!videoId || isReplyPreview) return null

        const fixedStaticGeometry = mediaBucketsEnabled()
        // Hosted playback never mounts an iframe here; the preview stays and
        // the dock's runtime is placed over it.
        const renderInlinePlayer = isLoaded && !this.hostedPlayback
        const hostedInline = this.state.hostedInline
        return (
            <div
                className={containerClassName}
                onClick={ev =>
                    suppressMediaPayloadContentClick(ev, {
                        preventDefault: true,
                    })
                }
            >
                <div
                    className="youtube-container"
                    style={
                        fixedStaticGeometry
                            ? { height: RESERVED_YOUTUBE_EMBED_HEIGHT, paddingBottom: 0 }
                            : undefined
                    }
                >
                    <div
                        ref={this.originRef}
                        className={cn('youtube-video', styles.youtubeVideoFrame)}
                        // The frame the dock-owned placeholder stands in for.
                        data-dock-placeholder-host=""
                        data-media-track-origin={this.getMediaKey() || undefined}
                        data-post-embed-navigation-exempt="true"
                        data-answer-id={`${answer?.id}`}
                        data-testid="answer-youtube-preview-frame"
                        onMouseEnter={this.handlePanelChromeMouseEnter}
                        onMouseMove={this.handlePanelChromeMouseMove}
                        onMouseLeave={this.handlePanelChromeMouseLeave}
                        onFocusCapture={this.handlePanelChromeFocus}
                        onBlurCapture={this.handlePanelChromeBlur}
                    >
                        {renderInlinePlayer ? (
                            <YouTube
                                key={videoId}
                                containerClassName="react-youtube-container"
                                videoId={videoId}
                                opts={{
                                    width: '100%',
                                    height: '100%',
                                    playerVars: buildInlineYouTubePlayerVars({
                                        start: this.props.vidStart,
                                    }),
                                }}
                                onReady={this.handleReady}
                                onStateChange={this.handleStateChange}
                                onPlaybackRateChange={this.handlePlaybackRateChange}
                                onPause={this.handlePause}
                                onEnd={this.handleEnd}
                            />
                        ) : (
                            <LazyYoutube
                                videoId={videoId}
                                title={url?.title}
                                onPlay={this.loadAndPlay}
                            />
                        )}
                        {renderInlinePlayer || hostedInline ? (
                            <div
                                className={styles.youtubeFullscreenIntentLayer}
                                aria-hidden="true"
                                onClick={this.handlePanelFullscreenPointerClick}
                                onMouseMove={this.handlePanelChromeMouseMove}
                            />
                        ) : null}
                        {hostedInline ? (
                            this.renderHostedChrome()
                        ) : (
                            <>
                                {this.renderMediaMenu()}
                                {renderInlinePlayer ? this.renderPanelControls() : null}
                            </>
                        )}
                        {this.renderDockOverlay()}
                    </div>
                </div>
            </div>
        )
    }
}

export default AnswerYouTubePlayer
